import { useCombobox } from "downshift";
import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { useEventListener } from "src/library/hooks/useEventListener";
import useOutSideItemClick from "src/library/hooks/useOutsideItemClick";
import { Label } from "src/library/components/Forms";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { useSearchApi } from "src/core/shared/api/search.api";
import { IStore } from "src/core/shared/store/index";
import useList from "src/core/lists/hooks/useList";
import useOrder from "src/core/orders/hooks/useOrder";
import useProductSearchFilters from "src/core/products/hooks/useProductSearchFilters";
import type { IProduct } from "src/core/products/api/products.api";
import { ProductStatus } from "src/core/products/api/products.api";

import ProductSearchComponent, { SearchRowProps } from "./ProductSearchComponent";

export type ISearchRequest = string;

type SearchPropsType = Pick<SearchRowProps, Exclude<keyof SearchRowProps, "product" | "highlighted" | "disabled">>;

interface Props {
  onProductSelect: (product: IProduct | null, quantity: number) => void;
  backToProductsButton: boolean;
  showProductsToggle: boolean;
  ProductAddItemComponent: React.ComponentType<{
    product: IProduct;
    highlighted: boolean;
    disabled: boolean;
    searchProps: SearchPropsType;
    onProductSelect: (product: IProduct | null, quantity: number) => void;
    setIsOpen: (open: boolean) => void;
  }>;
  label?: string;
}

export const OrderQuickProductAdd: React.ReactFCWithChildren<Props> = ({
  onProductSelect,
  backToProductsButton,
  ProductAddItemComponent,
  label,
  showProductsToggle
}) => {
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const [ { currentOrder } ] = useOrder();
  const [ { currentList } ] = useList();
  const history = useHistory();
  const [ hideNotAvailable, setHideNotAvailable ] = React.useState(true);
  const productSearchFilters = useProductSearchFilters();
  const [ totalHidden, setTotalHidden ] = React.useState(0);

  const { terminology, priceCodeId } = useMappedState(({ global, drops }: IStore) => ({
    terminology: global.settings && global.settings.terminology,
    priceCodeId: drops.drop && drops.drop.priceCodeId,
    installedModules: global.settings ? global.settings.installedModules : []
  }));

  const [ isOpen, setIsOpen ] = React.useState<boolean>(false);
  const [ highlightedIndex, setHighlighted ] = React.useState<number | null>(null);

  const searchResults = React.useMemo(() => {
    let hiddenCount = 0;

    if (listSearchResponse.data) {
      if (hideNotAvailable) {
        const results = _.filter(listSearchResponse.data.result, p => {
          if (p.currentStatus?.status === ProductStatus.Active) {
            return true;
          } else {
            hiddenCount++;

            return false;
          }
        });

        setTotalHidden(hiddenCount);

        return results;
      } else {
        setTotalHidden(hiddenCount);

        return listSearchResponse.data.result;
      }
    }

    setTotalHidden(hiddenCount);

    return [];
  }, [ listSearchResponse.data, hideNotAvailable ]);

  const downShiftProps = useCombobox<IProduct>({
    items: searchResults,
    isOpen,
    itemToString: () => "",
    onHighlightedIndexChange: changes => {
      if (typeof changes.highlightedIndex === "number") {
        setHighlighted(changes.highlightedIndex);
      }
    },
    onSelectedItemChange: () => {
      if (typeof highlightedIndex === "number") {
        setHighlighted(highlightedIndex);
      }
    }
  });

  const searchRef = React.useRef<HTMLDivElement>(null);

  useOutSideItemClick(searchRef, () => {
    setIsOpen(false);
  });

  /**
   * Finds available products to add to the change list
   *
   * @param {*} props
   * @returns
   */
  const performSearch = React.useCallback(
    (data: ISearchRequest) => {
      const filters = productSearchFilters(true);

      getSearch({
        pagination: {
          offset: 0,
          limit: 100,
          page: 0,
          sort: [ [ "description", "DESC" ] ]
        },
        filters: {
          ...filters,
          $or: [ { description: { $like: `%${data}%` } }, { partNumber: { $like: `%${data}%` } } ]
        }
      });
      setIsOpen(true);
    },
    [
      priceCodeId,
      hideNotAvailable,
      setIsOpen,
      productSearchFilters
    ]
  );

  /**
   * Get the row quantity
   */
  const getQuantity = React.useCallback(
    (product: IProduct) => {
      if (product) {
        if (currentOrder.data) {
          const orderLine = _.find(currentOrder.data.orderLines, ol => {
            return ol.productId === product.id;
          });

          if (orderLine) {
            return orderLine.quantity;
          }
        } else if (currentList.data) {
          const orderLine = _.find(currentList.data.listLines, listLine => listLine.productId === product.id);

          if (orderLine) {
            return orderLine.quantity;
          }
        }
      }

      return 0;
    },
    [ currentOrder.data, currentList.data ]
  );

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      if (typeof highlightedIndex === "number") {
        if (searchResults[ highlightedIndex ]) {
          if (currentOrder.data || currentList.data) {
            if (getQuantity(searchResults[ highlightedIndex ]) === 0) {
              onProductSelect(searchResults[ highlightedIndex ], 1);
            }
          } else {
            onProductSelect(searchResults[ highlightedIndex ], 1);
          }
        }

        setIsOpen(false);
      }
    }
  });

  return (
    <QuickProductAddWrapper>
      {backToProductsButton && (
        <div className="back-to-products">
          <Button onClick={() => history.push("/products")}>
            Browse products
          </Button>
        </div>
      )}

      <div
        className="product-search"
        ref={searchRef}
      >
        {label && (
          <Grid.Box>
            <Label
              for="search"
              label={label}
            />
          </Grid.Box>
        )}

        {terminology && (
          <ProductSearchComponent
            error={listSearchResponse.error}
            onSearch={performSearch}
            downshiftProps={downShiftProps}
            loading={listSearchResponse.pending}
            searchResults={searchResults}
            placeholder={`Search ${terminology.product.plural.text}... (ctrl+s)`}
            FooterComponent={() =>
              showProductsToggle ? (
                <FooterWrappper direction="horizontal">
                  <Text className="footer-text">
                    {hideNotAvailable ?
                      !!totalHidden && `${totalHidden} products were not shown because they are not available` :
                      "Showing all products, including unavailable."}
                  </Text>

                  <Grid.Box
                    className="show-hide"
                    onClick={() => setHideNotAvailable(!hideNotAvailable)}
                  >
                    <Text
                      weight="bold"
                      color="secondary"
                      className="show-hide-text"
                    >
                      {hideNotAvailable ? "Show unavailable products" : "Hide unavailble products"}
                    </Text>
                  </Grid.Box>
                </FooterWrappper>
              ) : null}
            SearchRowComponent={React.memo(({
              product, highlighted, disabled, ...searchProps
            }) => {
              return (
                <ProductAddItemComponent
                  onProductSelect={onProductSelect}
                  product={product}
                  highlighted={highlighted}
                  disabled={disabled}
                  searchProps={searchProps}
                  setIsOpen={setIsOpen}
                />
              );
            })}
          />
        )}
      </div>
    </QuickProductAddWrapper>
  );
};

const FooterWrappper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    width: 100%;
    flex-direction: row;
    align-content: center;
    justify-content: space-between;

    .show-hide {
      &:hover {
        cursor: pointer;
        .show-hide-text {
          color: ${theme.colors.primaryDark};
        }
      }
    }
  `}
`;

const QuickProductAddWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    border-radius: ${theme.borderRadius};
    padding-right: ${theme.spacing.padding.md};

    .back-to-products {
      display: none;
      margin-right: ${theme.spacing.margins.md};
    }

    ${breakpoint("lg", theme)} {
      display: block;
    }
  `}
`;
